/**
 * Monte Carlo variable definitions — shared between UI and Web Worker.
 * Each variable has a serializable definition (key, label, range) and an
 * `apply` function that mutates a Scenario clone. The apply functions are
 * available at import time so workers never need to serialize closures.
 */

import type { Scenario } from "../types";
import type { MonteCarloVariable } from "../model";

export const DEFAULT_MC_VARIABLES: MonteCarloVariable[] = [
  {
    key: "constructionCost",
    label: "Construction cost / sqm",
    range: 0.15,
    apply: (s: Scenario, f: number) => {
      s.buildings = s.buildings.map((b) => ({
        ...b,
        constructionPerSqmGfa: b.constructionPerSqmGfa * f,
      }));
    },
  },
  {
    key: "capRate",
    label: "Cap rate",
    range: 0.2,
    apply: (s: Scenario, f: number) => {
      s.assumptions.capRate = Math.max(0.02, s.assumptions.capRate * f);
    },
  },
  {
    key: "purchasePrice",
    label: "Purchase price",
    range: 0.1,
    apply: (s: Scenario, f: number) => {
      s.assumptions.purchasePrice = Math.max(
        0,
        s.assumptions.purchasePrice * f,
      );
    },
  },
  {
    key: "startingUtilisation",
    label: "Starting utilisation",
    range: 0.15,
    apply: (s: Scenario, f: number) => {
      s.assumptions.startingUtilisation = Math.max(
        0.1,
        Math.min(1, s.assumptions.startingUtilisation * f),
      );
    },
  },
  {
    key: "steadyStateUtilisation",
    label: "Steady-state utilisation",
    range: 0.08,
    apply: (s: Scenario, f: number) => {
      s.assumptions.steadyStateUtilisation = Math.max(
        0.1,
        Math.min(1, s.assumptions.steadyStateUtilisation * f),
      );
    },
  },
  {
    key: "tuitionGrowth",
    label: "Tuition growth",
    range: 0.25,
    apply: (s: Scenario, f: number) => {
      s.assumptions.tuitionGrowthPct = Math.max(
        -0.02,
        s.assumptions.tuitionGrowthPct * f,
      );
    },
  },
  {
    key: "feePerStudent",
    label: "Fee per student Y1",
    range: 0.1,
    apply: (s: Scenario, f: number) => {
      s.assumptions.feePerStudentYear1 = Math.max(
        10000,
        s.assumptions.feePerStudentYear1 * f,
      );
    },
  },
  {
    key: "fixedOpex",
    label: "Fixed opex",
    range: 0.12,
    apply: (s: Scenario, f: number) => {
      s.assumptions.fixedOpex = Math.max(100000, s.assumptions.fixedOpex * f);
    },
  },
];

/** Look up a variable definition by key. Throws if not found (fail-fast). */
export function getMcVariable(key: string): MonteCarloVariable {
  const v = DEFAULT_MC_VARIABLES.find((v) => v.key === key);
  if (!v) throw new Error(`Unknown MC variable key: ${key}`);
  return v;
}

/** Build a MonteCarloVariable[] from a list of enabled keys and custom ranges. */
export function buildMcVariables(
  enabledKeys: string[],
  ranges?: Record<string, number>,
): MonteCarloVariable[] {
  return enabledKeys.map((key) => {
    const base = getMcVariable(key);
    const range = ranges?.[key] ?? base.range;
    return { ...base, range };
  });
}
